import { Node, mergeAttributes } from '@tiptap/core';

/**
 * SceneBlock: a drawn figure that carries an idea the prose is making.
 *
 * Not decoration and not a stock illustration. Each scene is built for one
 * argument, and the drawing has to encode it — the first one, 'aim', exists
 * because Romans 12:18 makes a distinction ("so far as it depends on you")
 * that a picture can hold in one frame: two arrows at the same target, one
 * yours and landing, one not yours to throw.
 *
 * Attrs:
 *  - scene: key into SCENES
 *  - caption: optional line under the figure (also the accessible description)
 */
const SCENES = {
  aim: `
    <svg viewBox="0 0 420 190" role="img" preserveAspectRatio="xMidYMid meet">
      <circle cx="300" cy="95" r="62" fill="#E4EFEC"/>
      <circle cx="300" cy="95" r="42" fill="#fff"/>
      <circle cx="300" cy="95" r="23" fill="#E4EFEC"/>
      <circle cx="300" cy="95" r="8" fill="#2B6B5F"/>
      <circle cx="300" cy="95" r="62" fill="none" stroke="#2B6B5F" stroke-width="2"/>
      <circle cx="300" cy="95" r="42" fill="none" stroke="#789078" stroke-width="1.5"/>
      <circle cx="300" cy="95" r="23" fill="none" stroke="#789078" stroke-width="1.5"/>
      <!-- the arrow that is yours to throw: it lands -->
      <g stroke="#C4451A" stroke-width="3.5" stroke-linecap="round" fill="none">
        <path d="M40 95h250"/>
      </g>
      <path d="M300 95l-16-8v16z" fill="#C4451A"/>
      <!-- Fletching, not a second head: angled back along the shaft so the
           arrow reads as pointing one way only. -->
      <g stroke="#C4451A" stroke-width="2.4" stroke-linecap="round">
        <path d="M40 95l16-9M40 95l16 9M50 95l16-9M50 95l16 9"/>
      </g>
      <!-- the arrow that is not: it falls short, and that is not a failure -->
      <g stroke="#B9C6BF" stroke-width="3" stroke-linecap="round" stroke-dasharray="7 7" fill="none">
        <path d="M48 152h150"/>
      </g>
      <path d="M206 152l-14-7v14z" fill="#B9C6BF"/>
    </svg>`,
};

export const SceneBlock = Node.create({
  name: 'sceneBlock',
  group: 'block',
  atom: true,

  addAttributes() {
    return {
      scene: {
        default: 'aim',
        parseHTML: (el) => el.getAttribute('data-scene') || 'aim',
        renderHTML: (a) => ({ 'data-scene': a.scene || 'aim' }),
      },
      caption: {
        default: '',
        parseHTML: (el) => el.getAttribute('data-caption') || '',
        renderHTML: (a) => ({ 'data-caption': a.caption || '' }),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-type="scene-block"]' }];
  },

  // The figure is markup, and a ProseMirror render spec cannot carry raw SVG,
  // so this path emits the wrapper and the node view paints it. Anywhere the
  // node view does not run, the caption still carries the meaning as text.
  renderHTML({ HTMLAttributes, node }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, { 'data-type': 'scene-block', class: 'scene-block' }),
      ['div', { class: 'scene-block-art' }],
      ...(node.attrs.caption
        ? [['div', { class: 'scene-block-caption' }, node.attrs.caption]]
        : []),
    ];
  },

  addNodeView() {
    return ({ node }) => {
      const dom = document.createElement('div');
      dom.className = 'scene-block';
      dom.setAttribute('data-type', 'scene-block');
      dom.setAttribute('data-scene', node.attrs.scene || 'aim');
      const art = document.createElement('div');
      art.className = 'scene-block-art';
      art.innerHTML = SCENES[node.attrs.scene] || '';
      const svg = art.querySelector('svg');
      if (svg) svg.setAttribute('aria-label', node.attrs.caption || 'Illustration');
      dom.appendChild(art);
      if (node.attrs.caption) {
        const cap = document.createElement('div');
        cap.className = 'scene-block-caption';
        cap.textContent = node.attrs.caption;
        dom.appendChild(cap);
      }
      return { dom };
    };
  },
});

export default SceneBlock;
